<script setup lang="ts">
import { toast } from '@/components/ui/toast';
import { trackEvent } from '@/utils/analytics';
import Option from '@/components/manage-appointment/Option.vue';
import { CalendarPlus2 } from 'lucide-vue-next';
import { store } from '@/store/store';

const props = defineProps<{
    startTime: Date;
    endTime: Date;
    doctorName: string;
    isInPerson: boolean;
}>();

function generateFileName() {
    try {
        const date = props.startTime.toLocaleString('en-CA', {
            month: 'short',
            day: '2-digit',
        });
        const doctor = props.doctorName;

        return `${doctor} - ${date}.ics`;
    } catch (err) {
        return 'calendar.ics';
    }
}

async function generateFile() { 
    const filename = generateFileName();

    let eventData = {
        start: props.startTime,
        end: props.endTime,
        summary: 'Virtual Doctor Appointment',
        description: `Virtual appointment with ${props.doctorName}. The doctor will call you.`,
        location: 'call'
    }

    if (props.isInPerson) {
        eventData.summary = 'In-Person Doctor Appointment';
        eventData.description = `In-person appointment with ${props.doctorName}. Make sure to arrive on time. Address: ${store.in_person_address}`;
        eventData.location = store.in_person_address;
    }

    try {
        const icalendarModule = await import('ical-generator');
        const icalendar = icalendarModule.default || icalendarModule;
        const calendar = icalendar({
            events: [
                eventData
            ]
        });

        const blob = new Blob([calendar.toString()], { type: 'text/calendar;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        trackEvent<{
            method: string,
            name: string,
        }>('add_to_calendar', {
            method: 'ics_download',
            name: 'Add to Calendar',
        });
    } catch (err) {
        toast({
            title: 'Failed to generate file. Please try again.',
            variant: 'destructive',
        });
    }
}
</script>

<template>
    <Option 
        title="Add to Calendar"
        subtitle="Save this appointment to your calendar."
        @click="generateFile"
    >
        <CalendarPlus2 class="size-5" />
    </Option>
</template>
